import React, { useState } from "react";
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, ResponsiveContainer, ReferenceLine, Tooltip, Cell, LabelList } from "recharts";
import { toast } from "sonner";
import { useRoundData } from "@/components/strategy/useRound";
import { useStrategy } from "@/context/StrategyContext";
import { Panel, PageHead, Loading, ErrorNote, Drawer, KindBadge, Field, DemoBadge, Avatar } from "@/components/strategy/ui";
import { sapi, errText, QUADRANT, PARTICIPANT_NAME } from "@/lib/strategy";

function Facts({ roundId }) {
  const { data, setData, loading } = useRoundData("facts");
  const [editing, setEditing] = useState(null);
  if (loading || !data) return null;
  const save = async (facts) => {
    try { const { data: d } = await sapi.put(`/rounds/${roundId}/facts`, { facts }); setData(d); setEditing(null); toast.success("Business context updated"); } catch (e) { toast.error(errText(e)); }
  };
  return (
    <Panel className="p-6" data-testid="facts-panel">
      <div className="flex items-center justify-between mb-4"><div><p className="st-caps">Business context</p><p className="st-muted text-xs mt-1">Confirmed facts, leadership estimates, assumptions and unknowns that inform — but never prejudge — the assessment.</p></div></div>
      <div className="space-y-2">
        {data.map((f, i) => (
          <div key={f.id} className="st-panel-2 p-3 grid sm:grid-cols-[1fr_1.4fr_auto] gap-3 items-start" data-testid={`fact-${f.id}`}>
            {editing === f.id ? (
              <>
                <input className="st-input !py-1.5 text-sm" value={f.label} onChange={(e) => setData(data.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                <div className="space-y-2">
                  <input className="st-input !py-1.5 text-sm" value={f.value} onChange={(e) => setData(data.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))} data-testid={`fact-${f.id}-value`} />
                  <input className="st-input !py-1.5 text-xs" value={f.note || ""} placeholder="Note" onChange={(e) => setData(data.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} />
                </div>
                <div className="flex flex-col gap-2">
                  <select className="st-select !py-1.5 text-xs" value={f.kind} onChange={(e) => setData(data.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))} data-testid={`fact-${f.id}-kind`}>{["FACT", "ESTIMATE", "ASSUMPTION", "UNKNOWN"].map((k) => <option key={k}>{k}</option>)}</select>
                  <button className="st-btn st-btn-primary !py-1.5 text-xs" onClick={() => save(data)} data-testid={`fact-${f.id}-save`}>Save</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold">{f.label}</p>
                <div><p className="text-sm">{f.value}</p>{f.note && <p className="st-faint text-xs mt-0.5">{f.note}</p>}</div>
                <div className="flex items-center gap-2"><KindBadge kind={f.kind} small /><button className="st-btn st-btn-quiet !py-1 !px-2 text-xs" onClick={() => setEditing(f.id)} data-testid={`fact-${f.id}-edit`}>Edit</button></div>
              </>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
}

export default function Opportunities() {
  const { round, roundId } = useStrategy();
  const { data, setData, loading, error, reload } = useRoundData("opportunities");
  const [sel, setSel] = useState(null);
  const [ov, setOv] = useState(null);
  if (loading || !data) return <Loading label="Scoring opportunities" />;
  if (error) return <ErrorNote error={error} retry={reload} />;

  const current = data.opportunities.find((o) => o.id === sel);
  const points = data.opportunities.filter((o) => o.impact != null).map((o) => ({ ...o, x: o.effort, y: o.impact, z: o.score }));
  const saveOverride = async () => {
    try {
      const { data: d } = await sapi.put(`/rounds/${roundId}/opportunities/override`, { opportunity: sel, criterion: ov.criterion, value: ov.kind === "UNKNOWN" ? null : Number(ov.value), kind: ov.kind, note: ov.note });
      setData(d); setOv(null); toast.success("Assumption updated — scores recalculated");
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-8">
      <PageHead eyebrow="Commercial opportunity engine" title="Opportunity matrix" sub="Ten opportunities scored against ten weighted criteria. Facts, leadership estimates, assumptions and unknowns are labelled; unknowns are excluded rather than guessed, and any score relying on assumptions is marked provisional."
        actions={<>{round?.demo && <DemoBadge />}{!data.participants_included?.length && <span className="st-pill" style={{ background: "rgba(240,181,92,.14)", color: "var(--st-amber)" }}>No submitted assessments yet — leadership criteria unknown</span>}</>} />

      <section className="grid lg:grid-cols-[1.25fr_1fr] gap-6">
        <Panel className="p-2 sm:p-4 overflow-x-auto" data-testid="opp-table">
          <table className="st-table">
            <thead><tr><th>#</th><th>Opportunity</th><th>Score</th><th>Quadrant</th><th>Status</th></tr></thead>
            <tbody>
              {data.opportunities.map((o) => (
                <tr key={o.id} className="st-row-click" onClick={() => setSel(o.id)} data-testid={`opp-row-${o.id}`}>
                  <td className="st-faint font-bold">{o.rank}</td>
                  <td><p className="font-semibold">{o.name}</p><p className="st-faint text-xs mt-0.5 leading-snug max-w-xs">{o.blurb}</p></td>
                  <td><span className="text-xl font-extrabold" style={{ color: QUADRANT[o.quadrant]?.color }}>{o.score}</span><span className="st-faint text-xs"> /100</span></td>
                  <td><span className="st-pill" style={{ color: QUADRANT[o.quadrant].color, background: `${QUADRANT[o.quadrant].color}1f` }}>{QUADRANT[o.quadrant].label}</span></td>
                  <td>{o.provisional ? <span className="st-pill" style={{ color: "var(--st-amber)", background: "rgba(240,181,92,.14)" }}>Provisional{o.unknown_count ? ` · ${o.unknown_count} unknown` : ""}</span> : <span className="st-pill" style={{ color: "var(--st-lime)", background: "var(--st-lime-dim)" }}>Grounded</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
        <Panel className="p-6 st-recharts" data-testid="opp-matrix">
          <p className="st-caps mb-1">Impact vs effort</p>
          <p className="st-faint text-xs mb-3">Vertical axis = impact (recurring revenue, scalability, strategic importance, margin). Horizontal = effort (inverse of speed, simplicity and low investment).</p>
          <div style={{ height: 340 }}>
            <ResponsiveContainer>
              <ScatterChart margin={{ top: 16, right: 16, bottom: 8, left: -10 }}>
                <XAxis type="number" dataKey="x" domain={[0, 10]} name="Effort" tickCount={6} label={{ value: "Effort →", position: "insideBottomRight", offset: -4, fill: "rgba(243,239,230,.5)", fontSize: 11 }} />
                <YAxis type="number" dataKey="y" domain={[0, 10]} name="Impact" tickCount={6} />
                <ZAxis type="number" dataKey="z" range={[80, 420]} />
                <ReferenceLine x={5} stroke="rgba(243,239,230,.15)" strokeDasharray="4 4" />
                <ReferenceLine y={6} stroke="rgba(243,239,230,.15)" strokeDasharray="4 4" />
                <Tooltip cursor={false} content={({ active, payload }) => active && payload?.length ? <div className="st-tooltip"><b>{payload[0].payload.name}</b><br />Score {payload[0].payload.score} · impact {payload[0].payload.impact} · effort {payload[0].payload.effort}<br /><span style={{ color: QUADRANT[payload[0].payload.quadrant].color }}>{QUADRANT[payload[0].payload.quadrant].label}</span></div> : null} />
                <Scatter data={points} onClick={(p) => setSel(p.id)} cursor="pointer">
                  {points.map((p) => <Cell key={p.id} fill={QUADRANT[p.quadrant].color} fillOpacity={0.85} />)}
                  <LabelList dataKey="rank" position="center" style={{ fill: "#0B0E13", fontSize: 10, fontWeight: 800 }} />
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs st-muted">
            <p><b style={{ color: QUADRANT.quick_win.color }}>Top-left</b> · Quick wins</p><p><b style={{ color: QUADRANT.strategic_bet.color }}>Top-right</b> · Strategic bets</p>
            <p><b style={{ color: QUADRANT.longer_term.color }}>Bottom-left</b> · Longer-term</p><p><b style={{ color: QUADRANT.distraction.color }}>Bottom-right</b> · Distractions</p>
          </div>
        </Panel>
      </section>

      <Facts roundId={roundId} />
      <p className="st-faint text-xs leading-relaxed">{data.methodology}</p>

      <Drawer open={!!current} onClose={() => { setSel(null); setOv(null); }} title={current?.name || ""} width={640} testid="opp-drawer">
        {current && (
          <div className="space-y-5">
            <div className="flex items-center gap-4"><span className="text-5xl font-extrabold tracking-tight" style={{ color: QUADRANT[current.quadrant].color }}>{current.score}</span><div><p className="st-caps">Weighted score / 100</p><p className="text-sm st-muted mt-1">{QUADRANT[current.quadrant].label} · {current.provisional ? "provisional" : "grounded"}</p></div></div>
            <p className="st-muted text-sm leading-relaxed">{current.blurb}</p>
            <div className="st-panel-2 p-4">
              <p className="st-caps mb-2">Founder support (from assessment answers)</p>
              <div className="grid grid-cols-3 gap-2">{Object.entries(current.support).map(([pid, s]) => <div key={pid} className="flex items-center gap-2 text-sm"><Avatar pid={pid} size={20} />{PARTICIPANT_NAME[pid]} <b className="ml-auto">{s == null ? "—" : `${Math.round(s * 100)}%`}</b></div>)}</div>
            </div>
            <div>
              <p className="st-caps mb-2">Criteria · click a row to edit the assumption</p>
              <div className="space-y-1.5">
                {data.criteria.map((c) => {
                  const v = current.criteria[c.id];
                  return (
                    <button key={c.id} onClick={() => setOv({ criterion: c.id, value: v.value ?? 5, kind: v.kind === "UNKNOWN" ? "ESTIMATE" : v.kind, note: "" })} className="st-panel-2 w-full text-left p-3 flex items-center gap-3 hover:border-[rgba(200,247,74,.4)] transition-colors" data-testid={`opp-crit-${c.id}`}>
                      <div className="flex-1 min-w-0"><p className="text-sm font-semibold">{c.name} <span className="st-faint font-medium">· w{c.weight}</span></p><p className="st-faint text-xs truncate">{v.note}</p></div>
                      <KindBadge kind={v.kind} small />
                      <span className="font-extrabold w-10 text-right">{v.value == null ? "—" : v.value}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {ov && (
              <div className="st-panel p-4 space-y-3" data-testid="opp-override-form">
                <p className="font-bold text-sm">Edit · {data.criteria.find((c) => c.id === ov.criterion)?.name}</p>
                <p className="st-faint text-xs">{data.criteria.find((c) => c.id === ov.criterion)?.hint}</p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Value (0–10)"><input type="number" min={0} max={10} step={0.5} className="st-input" value={ov.value} disabled={ov.kind === "UNKNOWN"} onChange={(e) => setOv({ ...ov, value: e.target.value })} data-testid="opp-override-value" /></Field>
                  <Field label="Provenance"><select className="st-select" value={ov.kind} onChange={(e) => setOv({ ...ov, kind: e.target.value })} data-testid="opp-override-kind">{["FACT", "ESTIMATE", "ASSUMPTION", "UNKNOWN"].map((k) => <option key={k}>{k}</option>)}</select></Field>
                </div>
                <Field label="Note / evidence"><input className="st-input" value={ov.note} onChange={(e) => setOv({ ...ov, note: e.target.value })} placeholder="Where does this number come from?" data-testid="opp-override-note" /></Field>
                <div className="flex justify-end gap-2"><button className="st-btn st-btn-quiet" onClick={() => setOv(null)}>Cancel</button><button className="st-btn st-btn-primary" onClick={saveOverride} data-testid="opp-override-save">Save &amp; recalculate</button></div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
}
